import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  PackageCheck, Plus, Trash2, Loader2, Camera, Image as ImageIcon,
  AlertTriangle, CheckCircle2, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const INCIDENT_TYPES = [
  { value: "missing", label: "Faltante", color: "text-amber-700" },
  { value: "damaged", label: "Dañado", color: "text-red-700" },
  { value: "wrong_product", label: "Producto incorrecto", color: "text-rose-700" },
];

const incidentLabel = (type) =>
  INCIDENT_TYPES.find((t) => t.value === type)?.label || type;

/**
 * Diálogo de recepción de pedido de compra.
 *
 * Permite editar la cantidad recibida por ítem y registrar incidencias
 * (faltante, dañado, producto incorrecto) con foto opcional. Al confirmar,
 * llama a `POST /orders/{order_id}/receive` con el payload estructurado.
 */
export default function ReceiveOrderDialog({ open, onOpenChange, order, onSuccess, prefillFromOcr }) {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // {itemIdx, incIdx}
  const fileRefs = useRef({});

  // Initialize items state when order changes / dialog opens
  useEffect(() => {
    if (!order || !open) return;
    // Build map of OCR pre-fill by order item idx (if provided)
    const ocrByIdx = new Map();
    if (Array.isArray(prefillFromOcr)) {
      for (const d of prefillFromOcr) {
        if (typeof d.matched_order_item_idx === "number") {
          ocrByIdx.set(d.matched_order_item_idx, Number(d.quantity ?? d.qty ?? 0));
        }
      }
    }
    const initial = (order.items || []).map((it, idx) => {
      const orderedQty = Number(it.qty ?? it.quantity ?? 0);
      const ocrQty = ocrByIdx.get(idx);
      return {
        ingredient_id: it.ingredient_id,
        name: it.name || "Ingrediente",
        unit: it.unit || "ud",
        ordered_qty: orderedQty,
        qty_received: ocrQty != null ? ocrQty : orderedQty,
        incidents: [],
        from_ocr: ocrQty != null,
      };
    });
    setItems(initial);
    setNotes(prefillFromOcr ? "Recepción pre-rellenada desde OCR del albarán" : "");
  }, [order, open, prefillFromOcr]);

  const totalIncidents = useMemo(
    () => items.reduce((acc, it) => acc + (it.incidents?.length || 0), 0),
    [items],
  );
  const itemsWithDiscrepancy = useMemo(
    () => items.filter((it) => Number(it.qty_received) !== Number(it.ordered_qty)).length,
    [items],
  );

  const updateQtyReceived = (idx, val) => {
    const num = Math.max(0, Number(val) || 0);
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, qty_received: num } : it)));
  };

  const addIncident = (idx) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? {
              ...it,
              incidents: [
                ...it.incidents,
                { type: "missing", qty: 0, note: "", photo_url: null },
              ],
            }
          : it,
      ),
    );
  };

  const updateIncident = (itemIdx, incIdx, patch) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === itemIdx
          ? {
              ...it,
              incidents: it.incidents.map((inc, j) =>
                j === incIdx ? { ...inc, ...patch } : inc,
              ),
            }
          : it,
      ),
    );
  };

  const removeIncident = (itemIdx, incIdx) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === itemIdx
          ? { ...it, incidents: it.incidents.filter((_, j) => j !== incIdx) }
          : it,
      ),
    );
  };

  const handlePhotoUpload = async (itemIdx, incIdx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La foto supera los 5MB");
      return;
    }
    setUploadingPhoto({ itemIdx, incIdx });
    try {
      const r = await api.uploadImage(file);
      if (r?.success && r.image_url) {
        updateIncident(itemIdx, incIdx, { photo_url: r.image_url });
        toast.success("Foto adjunta");
      } else {
        toast.error("No se pudo subir la foto");
      }
    } catch {
      toast.error("Error al subir la foto");
    } finally {
      setUploadingPhoto(null);
    }
  };

  const triggerFileInput = (itemIdx, incIdx) => {
    const key = `${itemIdx}-${incIdx}`;
    fileRefs.current[key]?.click();
  };

  const handleSubmit = async () => {
    if (!order?.order_id) return;
    // Validate: every incident must have qty > 0
    const invalid = items.some((it) =>
      it.incidents.some((inc) => Number(inc.qty) <= 0),
    );
    if (invalid) {
      toast.error("Cada incidencia debe tener una cantidad mayor que 0");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        items: items.map((it) => ({
          ingredient_id: it.ingredient_id,
          qty_received: Number(it.qty_received) || 0,
          incidents: (it.incidents || []).map((inc) => ({
            type: inc.type,
            qty: Number(inc.qty) || 0,
            note: inc.note || "",
            photo_url: inc.photo_url || null,
          })),
        })),
        notes: notes.trim(),
      };
      const res = await api.receiveOrder(order.order_id, payload);
      toast.success(
        `Pedido recibido — ${res.movements_created} movimiento(s) de stock${
          res.incidents_count > 0 ? `, ${res.incidents_count} incidencia(s)` : ""
        }`,
      );
      onSuccess?.(res);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Error al registrar la recepción");
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="receive-order-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-emerald-700" />
            Recibir pedido
          </DialogTitle>
          <DialogDescription>
            {order.supplier_name || "Proveedor sin asignar"} ·{" "}
            <span className="text-xs">#{(order.order_id || "").slice(-8)}</span>
            {" · "}
            {(order.items || []).length} ítem(s)
          </DialogDescription>
        </DialogHeader>

        {/* Summary chips */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {itemsWithDiscrepancy > 0 ? (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 gap-1">
              <AlertTriangle className="w-3 h-3" />
              {itemsWithDiscrepancy} con discrepancia
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Sin discrepancias
            </Badge>
          )}
          {totalIncidents > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
              {totalIncidents} incidencia(s) registrada(s)
            </Badge>
          )}
        </div>

        {/* Items list */}
        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
          {items.map((it, idx) => {
            const discrepancy = Number(it.qty_received) !== Number(it.ordered_qty);
            return (
              <div
                key={`${it.ingredient_id}-${idx}`}
                className={`border rounded-lg p-3 space-y-3 ${
                  discrepancy ? "border-amber-300 bg-amber-50/30" : "border-border"
                }`}
                data-testid={`receive-item-row-${idx}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate flex items-center gap-1.5">
                      {it.name}
                      {it.from_ocr && (
                        <span className="inline-flex items-center text-[10px] text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded" title="Cantidad detectada por IA">
                          ✨ IA
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Pedido: <span className="font-mono">{it.ordered_qty}</span> {it.unit}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`qty-rec-${idx}`} className="text-xs whitespace-nowrap">
                      Recibido:
                    </Label>
                    <Input
                      id={`qty-rec-${idx}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.qty_received}
                      onChange={(e) => updateQtyReceived(idx, e.target.value)}
                      className="w-24 h-8 text-sm"
                      data-testid={`receive-qty-input-${idx}`}
                    />
                    <span className="text-xs text-muted-foreground">{it.unit}</span>
                  </div>
                </div>

                {/* Incidents */}
                {it.incidents.length > 0 && (
                  <div className="space-y-2 pl-2 border-l-2 border-red-200">
                    {it.incidents.map((inc, j) => {
                      const refKey = `${idx}-${j}`;
                      const isUploading =
                        uploadingPhoto?.itemIdx === idx && uploadingPhoto?.incIdx === j;
                      return (
                        <div
                          key={j}
                          className="bg-background border rounded-md p-2 space-y-2"
                          data-testid={`incident-row-${idx}-${j}`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <Select
                              value={inc.type}
                              onValueChange={(v) => updateIncident(idx, j, { type: v })}
                            >
                              <SelectTrigger
                                className="w-40 h-8 text-xs"
                                data-testid={`incident-type-${idx}-${j}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {INCIDENT_TYPES.map((t) => (
                                  <SelectItem key={t.value} value={t.value} className="text-xs">
                                    {t.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Cantidad"
                              value={inc.qty}
                              onChange={(e) =>
                                updateIncident(idx, j, { qty: e.target.value })
                              }
                              className="w-24 h-8 text-xs"
                              data-testid={`incident-qty-${idx}-${j}`}
                            />
                            <span className="text-xs text-muted-foreground">{it.unit}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeIncident(idx, j)}
                              className="h-8 w-8 p-0 ml-auto"
                              data-testid={`incident-remove-${idx}-${j}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          </div>
                          <Input
                            placeholder="Nota (opcional, máx. 300)"
                            maxLength={300}
                            value={inc.note}
                            onChange={(e) => updateIncident(idx, j, { note: e.target.value })}
                            className="h-8 text-xs"
                            data-testid={`incident-note-${idx}-${j}`}
                          />
                          <div className="flex items-center gap-2">
                            <input
                              ref={(el) => (fileRefs.current[refKey] = el)}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              onChange={(e) =>
                                handlePhotoUpload(idx, j, e.target.files?.[0])
                              }
                              data-testid={`incident-photo-input-${idx}-${j}`}
                            />
                            {inc.photo_url ? (
                              <div className="flex items-center gap-2">
                                <img
                                  src={inc.photo_url}
                                  alt="incidencia"
                                  className="w-12 h-12 rounded object-cover border"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    updateIncident(idx, j, { photo_url: null })
                                  }
                                  className="h-7 text-xs"
                                  data-testid={`incident-photo-remove-${idx}-${j}`}
                                >
                                  <X className="w-3 h-3 mr-1" /> Quitar foto
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => triggerFileInput(idx, j)}
                                disabled={isUploading}
                                className="h-7 text-xs gap-1"
                                data-testid={`incident-photo-add-${idx}-${j}`}
                              >
                                {isUploading ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Camera className="w-3 h-3" />
                                )}
                                Adjuntar foto
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addIncident(idx)}
                  className="text-xs gap-1 h-7"
                  data-testid={`add-incident-btn-${idx}`}
                >
                  <Plus className="w-3 h-3" /> Añadir incidencia
                </Button>
              </div>
            );
          })}
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <Label htmlFor="receive-notes" className="text-xs">
            Notas de recepción (opcional)
          </Label>
          <Textarea
            id="receive-notes"
            placeholder="Observaciones generales sobre la entrega..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            rows={2}
            className="text-sm"
            data-testid="receive-notes-input"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="receive-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || items.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="receive-confirm-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <PackageCheck className="w-4 h-4 mr-2" />
            )}
            Confirmar recepción
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
